/**
 * @fileoverview Error thrown when a direct-to-storage transfer fails after every retry.
 */

/**
 * Why a transfer gave up. 'no-etag' is a multipart part that landed without an
 * ETag the browser could read: the bucket's CORS policy stopped exposing the
 * header (docs/r2-cors.md), so no part upload can be assembled until it does.
 */
export type UploadFailureReason = 'network' | 'stalled' | 'aborted' | 'http' | 'no-etag'

/**
 * One PUT, as the browser saw it. Successful PUTs are recorded too (`reason:
 * 'ok'`), so a multipart transfer that fails on part 4 shows the three parts
 * that landed, their sizes and timings, not just the attempts on the part that
 * gave up.
 */
export interface UploadAttempt {
  attempt: number
  reason: UploadFailureReason | 'ok'
  status: number
  /** Bytes the browser reported sent: the whole body for 'ok', what got out before the request died otherwise. */
  bytesSent: number
  elapsedMs: number
  /** Set for a multipart part; absent for a single PUT. */
  partNumber?: number
}

/**
 * What Sentry needs to tell a dropped connection from an unreadable file: a
 * drop sends some bytes then dies at a different point each time; a file the
 * browser cannot read fails at once, every time, and the probe says so.
 */
export interface UploadDiagnostics {
  transfer: 'single' | 'multipart'
  /** 'part': a part PUT gave up; 'complete': the parts arrived but assembling them failed. */
  phase: 'put' | 'complete'
  /** Every PUT of the transfer, landed or not, plus the assembly calls for a multipart upload. */
  attemptLog: UploadAttempt[]
  /** Bytes sent on the attempt that gave up. */
  bytesSent: number
  /** Wall clock from the first attempt to giving up. */
  elapsedMs: number
  partNumber?: number
  partsDone?: number
  partCount?: number
  /** Whether the browser could still read the file's head and tail after the failure. */
  fileReadable: 'yes' | 'no' | 'unknown'
  online?: boolean
  connection?: { effectiveType?: string; downlink?: number; rtt?: number }
}

/**
 * A direct-to-storage transfer that failed after every retry. Operator copy
 * only — the message is never shown to users (see isPlumbingError); diagnostics
 * are fields.
 */
export class UploadTransferError extends Error {
  readonly status: number
  readonly reason: UploadFailureReason
  readonly attempts: number
  readonly diagnostics: UploadDiagnostics

  constructor(reason: UploadFailureReason, status: number, diagnostics: UploadDiagnostics) {
    super('Upload transfer failed')
    this.name = 'UploadTransferError'
    this.reason = reason
    this.status = status
    this.attempts = diagnostics.attemptLog.length
    this.diagnostics = diagnostics
  }
}

export function isUploadTransferError(err: unknown): err is UploadTransferError {
  return err instanceof UploadTransferError
}
